// Theme Style Configuration - Each theme has unique visual identity
// This file defines complete visual styles for each theme

export interface ThemeStyle {
  slug: string;
  // Typography
  fonts: {
    heading: string;
    body: string;
    headingWeight: number;
    bodyWeight: number;
  };
  // Border Radius
  radius: {
    card: string;
    button: string;
    badge: string;
    input: string;
  };
  // Shadows
  shadows: {
    card: string;
    button: string;
    hover: string;
  };
  // Layout
  layout: {
    heroStyle: 'centered' | 'split' | 'fullwidth' | 'minimal' | 'magazine' | 'editorial' | 'brutalist' | 'organic';
    productCardStyle: 'default' | 'minimal' | 'luxury' | 'neon' | 'glass' | 'bold' | 'organic' | 'sport';
    headerStyle: 'default' | 'transparent' | 'dark' | 'minimal' | 'luxe' | 'tech' | 'organic' | 'sport';
    sectionSpacing: 'compact' | 'normal' | 'spacious';
    containerWidth: 'narrow' | 'default' | 'wide' | 'full';
  };
  // Animations
  animations: {
    cardHover: string;
    pageTransition: string;
    buttonHover: string;
  };
  // Special Effects
  effects: {
    glassmorphism: boolean;
    gradientText: boolean;
    neonGlow: boolean;
    patternOverlay: boolean;
    customCursor: boolean;
  };
  // CSS Variables overrides
  cssVars: Record<string, string>;
}

export const themeStyles: Record<string, ThemeStyle> = {
  // ========== CLASSIC - Timeless Elegance ==========
  'classic': {
    slug: 'classic',
    fonts: {
      heading: "'Playfair Display', serif",
      body: "'Cairo', sans-serif",
      headingWeight: 700,
      bodyWeight: 400,
    },
    radius: {
      card: '1rem',
      button: '0.5rem',
      badge: '0.25rem',
      input: '0.5rem',
    },
    shadows: {
      card: '0 4px 20px rgba(0, 0, 0, 0.08)',
      button: '0 2px 8px rgba(0, 0, 0, 0.1)',
      hover: '0 12px 40px rgba(0, 0, 0, 0.15)',
    },
    layout: {
      heroStyle: 'centered',
      productCardStyle: 'default',
      headerStyle: 'default',
      sectionSpacing: 'normal',
      containerWidth: 'default',
    },
    animations: {
      cardHover: 'translateY(-4px)',
      pageTransition: 'fade',
      buttonHover: 'scale(1.02)',
    },
    effects: {
      glassmorphism: false,
      gradientText: false,
      neonGlow: false,
      patternOverlay: false,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': 'none',
      '--theme-card-border': '1px solid hsl(var(--border))',
    },
  },

  // ========== MODERN - Bold & Contemporary ==========
  'modern': {
    slug: 'modern',
    fonts: {
      heading: "'Space Grotesk', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 600,
      bodyWeight: 400,
    },
    radius: {
      card: '1.5rem',
      button: '9999px',
      badge: '9999px',
      input: '0.75rem',
    },
    shadows: {
      card: '0 8px 32px rgba(100, 60, 255, 0.12)',
      button: '0 4px 16px rgba(100, 60, 255, 0.2)',
      hover: '0 20px 60px rgba(100, 60, 255, 0.25)',
    },
    layout: {
      heroStyle: 'split',
      productCardStyle: 'glass',
      headerStyle: 'transparent',
      sectionSpacing: 'spacious',
      containerWidth: 'wide',
    },
    animations: {
      cardHover: 'translateY(-8px) rotateY(5deg)',
      pageTransition: 'slide',
      buttonHover: 'scale(1.05)',
    },
    effects: {
      glassmorphism: true,
      gradientText: true,
      neonGlow: false,
      patternOverlay: true,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
      '--theme-card-border': 'none',
    },
  },

  // ========== DARK PRO - Cyberpunk/Gaming ==========
  'dark-pro': {
    slug: 'dark-pro',
    fonts: {
      heading: "'Orbitron', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 700,
      bodyWeight: 400,
    },
    radius: {
      card: '0.25rem',
      button: '0.25rem',
      badge: '0.125rem',
      input: '0.25rem',
    },
    shadows: {
      card: '0 0 30px rgba(0, 255, 136, 0.15), inset 0 0 60px rgba(0, 0, 0, 0.3)',
      button: '0 0 20px rgba(0, 255, 136, 0.4)',
      hover: '0 0 50px rgba(0, 255, 136, 0.4)',
    },
    layout: {
      heroStyle: 'fullwidth',
      productCardStyle: 'neon',
      headerStyle: 'dark',
      sectionSpacing: 'normal',
      containerWidth: 'wide',
    },
    animations: {
      cardHover: 'translateY(-4px) scale(1.02)',
      pageTransition: 'glitch',
      buttonHover: 'scale(1.03)',
    },
    effects: {
      glassmorphism: true,
      gradientText: true,
      neonGlow: true,
      patternOverlay: true,
      customCursor: true,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%2300ff88' stroke-opacity='0.1'%3E%3Cpath d='M0 20h40M20 0v40'/%3E%3C/g%3E%3C/svg%3E\")",
      '--theme-card-border': '1px solid rgba(0, 255, 136, 0.2)',
      '--theme-scanline': 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,136,0.03) 2px, rgba(0,255,136,0.03) 4px)',
    },
  },

  // ========== LUXURY - Premium Gold ==========
  'luxury': {
    slug: 'luxury',
    fonts: {
      heading: "'Cormorant Garamond', serif",
      body: "'Cairo', sans-serif",
      headingWeight: 600,
      bodyWeight: 400,
    },
    radius: {
      card: '0.125rem',
      button: '0',
      badge: '0',
      input: '0',
    },
    shadows: {
      card: '0 20px 60px rgba(0, 0, 0, 0.3)',
      button: '0 4px 20px rgba(212, 175, 55, 0.3)',
      hover: '0 30px 80px rgba(212, 175, 55, 0.2)',
    },
    layout: {
      heroStyle: 'editorial',
      productCardStyle: 'luxury',
      headerStyle: 'luxe',
      sectionSpacing: 'spacious',
      containerWidth: 'narrow',
    },
    animations: {
      cardHover: 'scale(1.01)',
      pageTransition: 'fade',
      buttonHover: 'translateY(-2px)',
    },
    effects: {
      glassmorphism: false,
      gradientText: true,
      neonGlow: false,
      patternOverlay: true,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30 30 60 0 30z' fill='%23D4AF37' fill-opacity='0.03'/%3E%3C/svg%3E\")",
      '--theme-card-border': '1px solid rgba(212, 175, 55, 0.3)',
      '--theme-gold-gradient': 'linear-gradient(135deg, #D4AF37 0%, #F4E5B2 50%, #D4AF37 100%)',
    },
  },

  // ========== MINIMAL - Swiss Design ==========
  'minimal': {
    slug: 'minimal',
    fonts: {
      heading: "'Helvetica Neue', 'Arial', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 500,
      bodyWeight: 400,
    },
    radius: {
      card: '0',
      button: '0',
      badge: '0',
      input: '0',
    },
    shadows: {
      card: 'none',
      button: 'none',
      hover: 'none',
    },
    layout: {
      heroStyle: 'minimal',
      productCardStyle: 'minimal',
      headerStyle: 'minimal',
      sectionSpacing: 'spacious',
      containerWidth: 'narrow',
    },
    animations: {
      cardHover: 'none',
      pageTransition: 'fade',
      buttonHover: 'none',
    },
    effects: {
      glassmorphism: false,
      gradientText: false,
      neonGlow: false,
      patternOverlay: false,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': 'none',
      '--theme-card-border': '1px solid hsl(var(--foreground))',
    },
  },

  // ========== VIBRANT - Pop Art / Playful ==========
  'vibrant': {
    slug: 'vibrant',
    fonts: {
      heading: "'Poppins', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 800,
      bodyWeight: 400,
    },
    radius: {
      card: '2rem',
      button: '9999px',
      badge: '9999px',
      input: '1rem',
    },
    shadows: {
      card: '8px 8px 0 hsl(var(--foreground))',
      button: '4px 4px 0 hsl(var(--foreground))',
      hover: '12px 12px 0 hsl(var(--foreground))',
    },
    layout: {
      heroStyle: 'magazine',
      productCardStyle: 'bold',
      headerStyle: 'default',
      sectionSpacing: 'normal',
      containerWidth: 'default',
    },
    animations: {
      cardHover: 'translate(-4px, -4px)',
      pageTransition: 'bounce',
      buttonHover: 'translate(-2px, -2px)',
    },
    effects: {
      glassmorphism: false,
      gradientText: false,
      neonGlow: false,
      patternOverlay: true,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='40' cy='40' r='20' fill='%23FF69B4' fill-opacity='0.1'/%3E%3C/svg%3E\")",
      '--theme-card-border': '3px solid hsl(var(--foreground))',
    },
  },

  // ========== TECH - Futuristic/Sci-Fi ==========
  'tech': {
    slug: 'tech',
    fonts: {
      heading: "'Rajdhani', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 600,
      bodyWeight: 400,
    },
    radius: {
      card: '0.5rem',
      button: '0.25rem',
      badge: '0.25rem',
      input: '0.25rem',
    },
    shadows: {
      card: '0 0 40px rgba(0, 200, 255, 0.15)',
      button: '0 0 20px rgba(0, 200, 255, 0.3)',
      hover: '0 0 60px rgba(0, 200, 255, 0.25)',
    },
    layout: {
      heroStyle: 'brutalist',
      productCardStyle: 'neon',
      headerStyle: 'tech',
      sectionSpacing: 'compact',
      containerWidth: 'full',
    },
    animations: {
      cardHover: 'rotateX(5deg) translateY(-8px)',
      pageTransition: 'slide',
      buttonHover: 'scale(1.05)',
    },
    effects: {
      glassmorphism: true,
      gradientText: true,
      neonGlow: true,
      patternOverlay: true,
      customCursor: true,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0v100M0 50h100' stroke='%2300C8FF' stroke-opacity='0.1' stroke-width='0.5'/%3E%3Ccircle cx='50' cy='50' r='3' fill='%2300C8FF' fill-opacity='0.2'/%3E%3C/svg%3E\")",
      '--theme-card-border': '1px solid rgba(0, 200, 255, 0.3)',
      '--theme-grid': 'repeating-linear-gradient(90deg, rgba(0,200,255,0.03) 0px, rgba(0,200,255,0.03) 1px, transparent 1px, transparent 50px)',
    },
  },

  // ========== NATURAL - Organic/Eco ==========
  'natural': {
    slug: 'natural',
    fonts: {
      heading: "'Fraunces', serif",
      body: "'Cairo', sans-serif",
      headingWeight: 600,
      bodyWeight: 400,
    },
    radius: {
      card: '2rem 0.5rem 2rem 0.5rem',
      button: '9999px',
      badge: '9999px',
      input: '1rem',
    },
    shadows: {
      card: '0 8px 32px rgba(34, 139, 34, 0.1)',
      button: '0 4px 16px rgba(34, 139, 34, 0.15)',
      hover: '0 16px 48px rgba(34, 139, 34, 0.2)',
    },
    layout: {
      heroStyle: 'organic',
      productCardStyle: 'organic',
      headerStyle: 'organic',
      sectionSpacing: 'spacious',
      containerWidth: 'default',
    },
    animations: {
      cardHover: 'translateY(-6px) rotate(1deg)',
      pageTransition: 'fade',
      buttonHover: 'scale(1.03)',
    },
    effects: {
      glassmorphism: false,
      gradientText: false,
      neonGlow: false,
      patternOverlay: true,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 10 Q70 30 50 50 Q30 70 50 90' stroke='%23228B22' stroke-opacity='0.05' fill='none' stroke-width='2'/%3E%3C/svg%3E\")",
      '--theme-card-border': '2px solid rgba(34, 139, 34, 0.15)',
    },
  },

  // ========== SPORT - Dynamic/Athletic ==========
  'sport': {
    slug: 'sport',
    fonts: {
      heading: "'Anton', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 400,
      bodyWeight: 400,
    },
    radius: {
      card: '0.25rem',
      button: '0.25rem',
      badge: '0.25rem',
      input: '0.25rem',
    },
    shadows: {
      card: '4px 4px 0 hsl(var(--primary))',
      button: '3px 3px 0 hsl(var(--foreground))',
      hover: '8px 8px 0 hsl(var(--primary))',
    },
    layout: {
      heroStyle: 'fullwidth',
      productCardStyle: 'sport',
      headerStyle: 'sport',
      sectionSpacing: 'compact',
      containerWidth: 'wide',
    },
    animations: {
      cardHover: 'translate(-4px, -4px) scale(1.02)',
      pageTransition: 'slide',
      buttonHover: 'translate(-2px, -2px)',
    },
    effects: {
      glassmorphism: false,
      gradientText: false,
      neonGlow: false,
      patternOverlay: true,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.03) 20px, rgba(255,255,255,0.03) 40px)',
      '--theme-card-border': '2px solid hsl(var(--primary))',
      '--theme-stripes': 'repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(0,0,0,0.03) 10px, rgba(0,0,0,0.03) 20px)',
    },
  },

  // ========== PROFESSIONAL - Business/Corporate ==========
  'professional': {
    slug: 'professional',
    fonts: {
      heading: "'Inter', sans-serif",
      body: "'Cairo', sans-serif",
      headingWeight: 600,
      bodyWeight: 400,
    },
    radius: {
      card: '0.75rem',
      button: '0.5rem',
      badge: '0.375rem',
      input: '0.5rem',
    },
    shadows: {
      card: '0 4px 24px rgba(59, 130, 246, 0.08)',
      button: '0 2px 12px rgba(59, 130, 246, 0.15)',
      hover: '0 12px 40px rgba(59, 130, 246, 0.15)',
    },
    layout: {
      heroStyle: 'split',
      productCardStyle: 'default',
      headerStyle: 'default',
      sectionSpacing: 'normal',
      containerWidth: 'default',
    },
    animations: {
      cardHover: 'translateY(-4px)',
      pageTransition: 'fade',
      buttonHover: 'scale(1.02)',
    },
    effects: {
      glassmorphism: true,
      gradientText: false,
      neonGlow: false,
      patternOverlay: false,
      customCursor: false,
    },
    cssVars: {
      '--theme-hero-pattern': 'none',
      '--theme-card-border': '1px solid hsl(var(--border))',
    },
  },
};

export const getThemeStyle = (slug: string): ThemeStyle => {
  return themeStyles[slug] || themeStyles['classic'];
};

// Generate Google Fonts URL for theme
export const getThemeFontsUrl = (slug: string): string => {
  const fontUrls: Record<string, string> = {
    'classic': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap',
    'modern': 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap',
    'dark-pro': 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&display=swap',
    'luxury': 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap',
    'minimal': '',
    'vibrant': 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap',
    'tech': 'https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap',
    'natural': 'https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600;700&display=swap',
    'sport': 'https://fonts.googleapis.com/css2?family=Anton&display=swap',
    'professional': 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  };
  return fontUrls[slug] || '';
};
